import React, { useState, useEffect } from "react";
import { ExternalLink, Trash2, PlusSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { hasPermission } from "../utils/permissions";

// ---------------- Header ----------------
const Header = ({ canAdd }) => {
  const navigate = useNavigate();

  const handleAddClick = () => {
    navigate("/AddDiagnosis");
  };

  return (
    <header className="main-header">
      <div className="header-left">
        <nav className="header-nav">
          <a href="DashBoard">Home</a>
          <span>/</span>
          <span>Diagnose</span>
        </nav>
      </div>
      <div className="header-right">
        {canAdd && (
          <button className="add-button" onClick={handleAddClick}>
            <PlusSquare size={18} />
          </button>
        )}
      </div>
    </header>
  );
};

// ---------------- DiagnoseTable ----------------
const DiagnoseTable = ({
  diagnoses,
  onEdit,
  onDelete,
  currentPage,
  itemsPerPage,
  canEdit,
  canDelete,
}) => {
  return (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>S No</th>
            <th>Diagnose Name</th>
                        <th>Category</th>

            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {diagnoses.length === 0 ? (
            <tr>
              <td
                colSpan="3"
                style={{ textAlign: "center", padding: "12px", color: "#666" }}
              >
                No diagnoses found
              </td>
            </tr>
          ) : (
            diagnoses.map((diagnose, index) => (
              <tr key={diagnose.diagnosis_id}>
                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td>{diagnose.diagnosis_name}</td>
                <td>{diagnose.category_name || 'N/A'}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => canEdit && onEdit(diagnose.diagnosis_id)}
                      title="Edit"
                      disabled={!canEdit}
                      style={{ opacity: canEdit ? 1 : 0.4, cursor: canEdit ? 'pointer' : 'not-allowed' }}
                    >
                      <ExternalLink size={18} />
                    </button>
                    <button
                      onClick={() => canDelete && onDelete(diagnose.diagnosis_id)}
                      title="Delete"
                      className="text-red-500 hover:text-red-700"
                      disabled={!canDelete}
                      style={{ opacity: canDelete ? 1 : 0.4, cursor: canDelete ? 'pointer' : 'not-allowed' }}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// ---------------- Pagination ----------------
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  // Calculate the start page of the 3-button window
  let startPage = Math.max(1, currentPage - 1);
  let endPage = Math.min(totalPages, startPage + 2);

  // Adjust startPage if we are at the end
  if (endPage - startPage < 2) {
    startPage = Math.max(1, endPage - 2);
  }

  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="pagination">
      <button
        className="page-btn prev"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        &laquo; Prev
      </button>

      {pages.map((num) => (
        <button
          key={num}
          className={`page-btn ${currentPage === num ? "active" : ""}`}
          onClick={() => onPageChange(num)}
        >
          {num}
        </button>
      ))}

      <button
        className="page-btn next"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next &raquo;
      </button>
    </div>
  );
};
// ---------------- Main Parent ----------------
const Diagnoses = () => {
  const navigate = useNavigate();
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Load permissions for diagnoses
  useEffect(() => {
    (async () => {
      try {
        const pView = (await hasPermission('master', 'diagnosis')) || (await hasPermission('master', 'viewdiagnosis'));
        const pAdd = await hasPermission('master', 'adddiagnosis');
        const pEdit = await hasPermission('master', 'editdiagnosis');
        const pDelete = await hasPermission('master', 'deletediagnosis');
        setCanView(!!pView);
        setCanAdd(!!pAdd);
        setCanEdit(!!pEdit);
        setCanDelete(!!pDelete);
      } catch (e) {
        setCanView(false);
        setCanAdd(false);
        setCanEdit(false);
        setCanDelete(false);
      }
    })();
  }, []);

  // Fetch diagnoses from API
  useEffect(() => {
    const fetchDiagnoses = async () => {
      try {
        const token = (localStorage.getItem('session_token') || '').trim();
        const response = await fetch(
          `https://newerp.awasthivitality.com/api/diagnosis/list${token ? `?token=${encodeURIComponent(token)}` : ''}`,
          {
            headers: {
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
              ...(token ? { 'X-Session-Token': token } : {}),
            }
          }
        );
        if (!response.ok) throw new Error("Failed to fetch diagnoses");
        const data = await response.json();
        setDiagnoses(data.data || []);
      } catch (error) {
        toast.error("Error fetching diagnoses!");
        console.error("Error fetching diagnoses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDiagnoses();
  }, []);

  // ✅ handleDelete with Toastify
const handleDelete = async (id) => {
  try {
    const token = (localStorage.getItem('session_token') || '').trim();
    const response = await fetch(
      `https://newerp.awasthivitality.com/api/diagnosis/delete/${id}${token ? `?token=${encodeURIComponent(token)}` : ''}`,
      {
        method: "DELETE",
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(token ? { 'X-Session-Token': token } : {}),
        }
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete diagnosis");
    }

    const result = await response.json();
    console.log("Delete API response:", result);

    // Remove deleted item from UI
    setDiagnoses((prev) =>
      prev.filter((diag) => diag.diagnosis_id !== id)
    );

    toast.success("Diagnosis deleted successfully!");
  } catch (error) {
    console.error("Error deleting diagnosis:", error);
    toast.error("Error deleting diagnosis. Please try again.");
  }
};


  const handleEdit = (diagnosis_id) => {
    navigate(`/EditDiagnosis/${diagnosis_id}`);
  };

  // Pagination logic
  const totalPages = Math.ceil(diagnoses.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDiagnoses = diagnoses.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => setCurrentPage(page);

  if (!canView) {
    return (
      <div className="page-container">
        <Header canAdd={false} />
        <div className="content-container" style={{ padding: '40px', textAlign: 'center' }}>
          <h3 style={{ color: '#e74c3c' }}>Access Denied</h3>
          <p>You do not have permission to view diagnoses.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Header canAdd={canAdd} />
      <div className="content-container">
        {loading ? (
          <p>Loading diagnoses...</p>
        ) : (
          <>
            <DiagnoseTable
              diagnoses={currentDiagnoses}
              onEdit={handleEdit}
              onDelete={handleDelete}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              canEdit={canEdit}
              canDelete={canDelete}
            />
            {diagnoses.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      {/* ✅ Toastify Container */}
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar />

      {/* ---------------- Plain CSS ---------------- */}
      <style>{`
        body {
          font-family: 'Arial', sans-serif;
          margin: 0;
          background: #f4f5f7;
        }

        .page-container {
          width: 100%;
          padding: 30px;
        }

        .main-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #2fb2e7;
          color: white;
          padding: 15px 20px;
          border-radius: 6px;
          margin-bottom: 20px;
          margin-left: 0;
        }

        @media (min-width: 1024px) {
          .main-header {
            margin-left: 230px;
            border-radius: 0 6px 6px 0;
          }
        }

        .header-nav a, .header-nav span {
          color: #f0f0f0;
          text-decoration: none;
          margin-right: 5px;
        }

        .add-button {
          background-color: #fff;
          color: #2fb2e7;
          border: none;
          padding: 8px 12px;
          display: flex;
          align-items: center;
          cursor: pointer;
          border-radius: 4px;
          font-weight: bold;
        }

        .add-button:hover {
          background-color: #e6f7ff;
        }

        .content-container {
          background: #fff;
          padding: 20px;
          border-radius: 6px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          margin-left: 0;
        }

        @media (min-width: 1024px) {
          .content-container {
            margin-left: 230px;
          }
        }

        .table-container {
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
        }

        th, td {
          padding: 6px 8px;
          border-bottom: 1px solid #ddd;
          text-align: left;
          vertical-align: middle;
          line-height: 1.4;
        }

        th {
          background-color: #f0f0f0;
          text-transform: uppercase;
          font-size: 0.8rem;
          font-weight: 600;
        }

        tr:hover {
          background-color: #f8f9fa;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          justify-content: flex-start;
          flex-wrap: wrap;
          align-items: center;
        }

        .action-buttons button {
          border: none;
          background: none;
          cursor: pointer;
          transition: transform 0.2s;
          display: flex;
          align-items: center;
        }

        .action-buttons button:hover {
          transform: scale(1.2);
          color: #2fb2e7;
        }

        .pagination {
          display: flex;
          justify-content: flex-end;
          gap: 6px;
          margin-top: 16px;
        }

        .page-btn {
          padding: 6px 12px;
          border: 1px solid #ddd;
          background-color: #fff;
          color: #2fb2e7;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .page-btn:hover:not(.active):not(:disabled) {
          background-color: #e6f7ff;
        }

        .page-btn.active {
          background-color: #2fb2e7;
          color: #fff;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default Diagnoses;
